import config from "services/config";

// ─── Validación de archivos ───────────────────────────────────────
export const MAX_FILE_BYTES = 50 * 1024 * 1024; // 50 MB
export const ALLOWED_MIME = new Set([
  "application/pdf",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/zip",
  "application/x-zip-compressed",
  "image/jpeg",
  "image/png",
  "image/webp",
  "text/csv",
  "text/plain",
]);

export function validateFile(file) {
  if (file.size > MAX_FILE_BYTES) {
    return `"${file.name}" excede 50 MB`;
  }
  if (file.type && !ALLOWED_MIME.has(file.type)) {
    return `Tipo no permitido: ${file.type || "desconocido"}`;
  }
  return null;
}

// ─── Upload con progreso real ─────────────────────────────────────
export function uploadFile({ file, folderId, onProgress, signal }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const form = new FormData();
    form.append("file", file);
    form.append("folderId", folderId);

    xhr.open("POST", `${config.url}/gdrive/upload-file`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); }
        catch { resolve({}); }
      } else {
        let detail = `HTTP ${xhr.status}`;
        try {
          const body = JSON.parse(xhr.responseText);
          detail = body.error || body.details || detail;
        } catch {}
        reject(new Error(detail));
      }
    };
    xhr.onerror = () => reject(new Error("Error de red"));
    xhr.onabort = () => reject(new Error("Subida cancelada"));

    if (signal) {
      if (signal.aborted) { xhr.abort(); return; }
      signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }

    xhr.send(form);
  });
}

// ─── Queue de uploads con concurrencia y retry exponencial ────────
export function createUploadQueue({ concurrency = 3, maxRetries = 2, onItemUpdate } = {}) {
  let nextId = 1;
  const items = new Map(); // id → { id, file, folderId, status, progress, error, result }
  const pending = [];
  let running = 0;

  const update = (id, patch) => {
    const it = items.get(id);
    if (!it) return;
    Object.assign(it, patch);
    if (onItemUpdate) onItemUpdate({ ...it });
  };

  const runOne = async (id) => {
    const it = items.get(id);
    if (!it) return;
    update(id, { status: "uploading", progress: 0, error: null });

    let attempt = 0;
    let lastErr = null;
    while (attempt <= maxRetries) {
      try {
        const result = await uploadFile({
          file: it.file,
          folderId: it.folderId,
          onProgress: (p) => update(id, { progress: p }),
        });
        update(id, { status: "done", progress: 1, result });
        return;
      } catch (err) {
        lastErr = err;
        attempt += 1;
        if (attempt > maxRetries) break;
        // Backoff: 500ms, 1500ms, 4500ms…
        await new Promise((r) => setTimeout(r, 500 * Math.pow(3, attempt - 1)));
      }
    }
    update(id, { status: "error", error: lastErr?.message || "Error desconocido" });
  };

  const drain = async () => {
    while (running < concurrency && pending.length > 0) {
      const id = pending.shift();
      running += 1;
      runOne(id).finally(() => {
        running -= 1;
        drain();
      });
    }
  };

  return {
    add(file, folderId) {
      const id = nextId++;
      items.set(id, { id, file, folderId, status: "queued", progress: 0, error: null, result: null });
      pending.push(id);
      if (onItemUpdate) onItemUpdate({ ...items.get(id) });
      drain();
      return id;
    },
    items: () => Array.from(items.values()),
    clearDone() {
      for (const [id, it] of items) {
        if (it.status === "done") items.delete(id);
      }
    },
  };
}

// ─── Endpoints helpers ────────────────────────────────────────────
async function jsonRequest(url, options = {}) {
  const res = await fetch(url, {
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options,
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      msg = body.error || body.details || msg;
    } catch {}
    throw new Error(msg);
  }
  return res.json();
}

export function renameFolder({ folderId, name, memberId }) {
  return jsonRequest(`${config.url}/gdrive/folder/${folderId}`, {
    method: "PATCH",
    body: JSON.stringify({ name, memberId }),
  });
}

export function deleteFolder({ folderId, memberId }) {
  return jsonRequest(`${config.url}/gdrive/folder/${folderId}`, {
    method: "DELETE",
    body: JSON.stringify({ memberId }),
  });
}

export function syncMemberDrive(memberId) {
  return jsonRequest(`${config.url}/gdrive/member/${memberId}/sync`, {
    method: "POST",
  });
}

export function fetchFolderFiles(folderId) {
  return jsonRequest(`${config.url}/gdrive/folder/${folderId}/files`, { method: "GET" });
}
